import { z } from "zod/v4";

export const RequirementStatusSchema = z.enum([
  "REQUIRED",
  "SUBMITTED",
  "APPROVED",
  "REJECTED",
]);

export type RequirementStatus = z.infer<typeof RequirementStatusSchema>;

export const CaseStatusSchema = z.enum(["OPEN", "COMPLETED"]);

export type CaseStatus = z.infer<typeof CaseStatusSchema>;

export const WorkflowStatusSchema = z.enum([
  "WAITING_FOR_INPUT",
  "PARTIAL",
  "PENDING_VOLUME_REVIEW",
  "APPROVED",
]);

export type WorkflowStatus = z.infer<typeof WorkflowStatusSchema>;

export const OverviewWorkflowStatusSchema = z.enum([
  "WAITING_FOR_INPUT",
  "PARTIAL",
  "PENDING_VOLUME_REVIEW",
  "APPROVED",
  "SENT_TO_OPTIMUS",
]);

export type OverviewWorkflowStatus = z.infer<
  typeof OverviewWorkflowStatusSchema
>;

export const MerchantSupportingInfoSchema = z.object({
  uploadId: z.string(),
  originalFileName: z.string(),
  sizeBytes: z.number(),
  uploadedAtIso: z.string(),
});

export type MerchantSupportingInfo = z.infer<
  typeof MerchantSupportingInfoSchema
>;

export const CollectionTypeSchema = z.enum(["file", "files", "text"]);

export type CollectionType = z.infer<typeof CollectionTypeSchema>;

const ComplianceUploadSchema = z
  .object({
    id: z.string(),
    Type: z.string().optional(),
    requirementId: z.string(),
    originalFileName: z.string(),
    contentType: z.string().nullable().optional(),
    sizeBytes: z.number(),
    uploadedBy: z.string(),
    uploadedAt: z.number(),
    uploadedAtIso: z.string(),
    checksum: z.string().nullable().optional(),
    isLatest: z.boolean(),
  })
  .transform((o) => ({
    ...o,
    /** @deprecated Use `id` instead */
    uploadId: o.id,
    /** @deprecated Use `sizeBytes` instead */
    fileSizeBytes: o.sizeBytes,
    /** ISO-formatted upload timestamp */
    uploadedAt: o.uploadedAtIso,
  }));

export type ComplianceUpload = z.infer<typeof ComplianceUploadSchema>;

export const ComplianceRequirementSchema = z
  .object({
    id: z.string(),
    requirementCode: z.string(),
    requirementDisplayName: z.string().optional(),
    requirementDescription: z.string().nullable().optional().default(null),
    requirementSupporting: z.array(z.string()).optional().default([]),
    collectionType: CollectionTypeSchema.optional(),
    /** Omitted in create-case response; defaults to REQUIRED for new requirements */
    status: RequirementStatusSchema.optional().default("REQUIRED"),
    /** Present when requirement status is REJECTED */
    rejectedReason: z.string().nullable().optional(),
    /** Admin-only: who reviewed this requirement */
    reviewedByAdminId: z.string().nullable().optional(),
    /** Admin-only: when the requirement was reviewed (epoch) */
    reviewedAt: z.number().nullable().optional(),
    /** Admin-only: when the requirement was reviewed (ISO) */
    reviewedAtIso: z.string().nullable().optional(),
    uploads: z.array(ComplianceUploadSchema).default([]),
  })
  .transform((o) => ({
    ...o,
    /** @deprecated Use `id` instead */
    requirementId: o.id,
    /** Friendly name for display — falls back to requirementCode */
    displayName: o.requirementDisplayName ?? o.requirementCode,
    /** Hint text for what to upload (from document type description) */
    description: o.requirementDescription ?? null,
    /** Supporting bullet points for what to include in responses */
    supporting: o.requirementSupporting,
    collectionType: o.collectionType ?? "file",
  }));

export type ComplianceRequirement = z.infer<typeof ComplianceRequirementSchema>;
export interface ComplianceRequirementUi {
  id: string;
  requirementCode: string;
  requirementDisplayName: string;
  requirementDescription: string | null;
  requirementSupporting: string[];
  collectionType: "file" | "files" | "text";
  status: "REQUIRED" | "SUBMITTED" | "APPROVED" | "REJECTED";
}

/** Extended schema for GET /api/admin/compliance/requirements/:id */
export const AdminRequirementDetailSchema = z
  .object({
    id: z.string(),
    caseId: z.string(),
    requirementCode: z.string(),
    requirementDisplayName: z.string().optional(),
    requirementDescription: z.string().nullable().optional().default(null),
    requirementSupporting: z.array(z.string()).optional().default([]),
    collectionType: CollectionTypeSchema.optional(),
    status: RequirementStatusSchema,
    rejectedReason: z.string().nullable().optional(),
    reviewedByAdminId: z.string().nullable().optional(),
    reviewedAt: z.number().nullable().optional(),
    reviewedAtIso: z.string().nullable().optional(),
    createdAt: z.number(),
    createdAtIso: z.string(),
    updatedAt: z.number(),
    updatedAtIso: z.string(),
    case: z.object({
      id: z.string(),
      applicationId: z.string(),
      merchantId: z.string().nullable(),
      customerFullName: z.string(),
    }),
    uploads: z.array(ComplianceUploadSchema).default([]),
  })
  .transform((o) => ({
    ...o,
    /** @deprecated Use `id` instead */
    requirementId: o.id,
    /** Friendly name for display — falls back to requirementCode */
    displayName: o.requirementDisplayName ?? o.requirementCode,
    /** Hint text for what to upload (from document type description) */
    description: o.requirementDescription ?? null,
    /** Supporting bullet points for what to include in responses */
    supporting: o.requirementSupporting,
    collectionType: o.collectionType ?? "file",
    /** ISO-formatted timestamps */
    createdAt: o.createdAtIso,
    updatedAt: o.updatedAtIso,
  }));

export type AdminRequirementDetail = z.infer<
  typeof AdminRequirementDetailSchema
>;

export const UploadToOptimusStatusSchema = z.enum([
  "PENDING",
  "SUCCESS",
  "FAILED",
]);

export type UploadToOptimusStatus = z.infer<typeof UploadToOptimusStatusSchema>;

export const UploadToOptimusItemSchema = z.object({
  id: z.string(),
  requestId: z.string(),
  status: UploadToOptimusStatusSchema,
  failureReason: z.string().nullable(),
  requestedByAdminId: z.string(),
  targetS3Key: z.string().nullable(),
  triggeredAt: z.number(),
  triggeredAtIso: z.string(),
  completedAt: z.number().nullable(),
  completedAtIso: z.string().nullable(),
  createdAt: z.number(),
  createdAtIso: z.string(),
  updatedAt: z.number(),
  updatedAtIso: z.string(),
});

export type UploadToOptimusItem = z.infer<typeof UploadToOptimusItemSchema>;

export const ComplianceCaseSchema = z
  .object({
    id: z.string(),
    applicationId: z.string(),
    merchantId: z.string().nullable(),
    paymentIds: z.array(z.string()).default([]),
    customerFullName: z.string(),
    status: CaseStatusSchema,
    workflowStatus: WorkflowStatusSchema.optional(),
    merchantSupportingInformation: z
      .array(MerchantSupportingInfoSchema)
      .default([]),
    transactionVolume: z.string().optional(),
    transactionVolumeCurrency: z.string().optional(),
    transactionCount: z.number().optional(),
    createdAt: z.number().optional(),
    createdAtIso: z.string().optional(),
    updatedAt: z.number().optional(),
    updatedAtIso: z.string().optional(),
    uploadedToOptimusAt: z.number().nullable().optional(),
    uploadedToOptimusAtIso: z.string().nullable().optional(),
    uploadsToOptimus: z.array(UploadToOptimusItemSchema).optional(),
    requirements: z.array(ComplianceRequirementSchema).default([]),
  })
  .transform((o) => ({
    ...o,
    /** @deprecated Use `id` instead */
    caseId: o.id,
    /** Mapped from `customerFullName` */
    customerName: o.customerFullName,
    paymentIds: o.paymentIds,
    transactionVolume: o.transactionVolume,
    transactionVolumeCurrency: o.transactionVolumeCurrency,
    transactionCount: o.transactionCount,
    /** ISO-formatted created timestamp */
    createdAt: o.createdAtIso,
    /** ISO-formatted updated timestamp */
    updatedAt: o.updatedAtIso,
  }));

export type ComplianceCase = z.infer<typeof ComplianceCaseSchema>;

/** Matches backend AdminOverviewItem */
const AdminOverviewItemSchema = z.object({
  merchantId: z.string().nullable(),
  applicationId: z.string(),
  submittedCount: z.number(),
  requiredCount: z.number(),
  rejectedCount: z.number(),
  waitingOnMerchantCount: z.number(),
  waitingOnUsCount: z.number(),
  submittedWithoutEvidenceCount: z.number(),
  totalTrackedCount: z.number(),
});

export const ComplianceOverviewSchema = z.array(AdminOverviewItemSchema);

export type AdminOverviewItem = z.infer<typeof AdminOverviewItemSchema>;
export type ComplianceOverview = z.infer<typeof ComplianceOverviewSchema>;

export const AdminWorkflowStatsSchema = z.object({
  workflowCounts: z.object({
    WAITING_FOR_INPUT: z.number(),
    PARTIAL: z.number(),
    PENDING_VOLUME_REVIEW: z.number(),
    APPROVED: z.number(),
    SENT_TO_OPTIMUS: z.number(),
  }),
  requirementCounts: z.object({
    REQUIRED: z.number(),
    SUBMITTED: z.number(),
    APPROVED: z.number(),
    REJECTED: z.number(),
  }),
});

export type AdminWorkflowStats = z.infer<typeof AdminWorkflowStatsSchema>;

export const ComplianceDocumentTypeSchema = z.object({
  code: z.string(),
  displayName: z.string(),
  description: z.string().nullable().optional(),
  supporting: z.array(z.string()).optional().default([]),
  isActive: z.boolean(),
  collectionType: CollectionTypeSchema.optional().default("file"),
});

export type ComplianceDocumentType = z.infer<
  typeof ComplianceDocumentTypeSchema
>;

export interface CursorPaginatedResult<T> {
  items: T[];
  nextCursor?: string;
  hasMore: boolean;
}

const CursorPaginationEnvelopeSchema = z.object({
  items: z.array(z.unknown()),
  nextCursor: z.string().optional(),
  hasMore: z.boolean(),
});

export function parseCursorResult<T>(
  input: unknown,
  itemSchema: z.ZodType<T>,
): CursorPaginatedResult<T> {
  const envelope = CursorPaginationEnvelopeSchema.parse(input);
  return {
    items: z.array(itemSchema).parse(envelope.items),
    nextCursor: envelope.nextCursor,
    hasMore: envelope.hasMore,
  };
}
